/* State management for the Smart Feed Cleaner feature. */

import { useState, useCallback, useMemo } from "react";
import { useAction, useMutation } from "convex/react";
import { api } from "../../convex/_generated/api";
import type { Id } from "../../convex/_generated/dataModel";
import type { CleanerResult } from "@/types/feedCleaner";

type ItemAction = "queue" | "dismiss";

export function useFeedCleaner() {
  const [result, setResult] = useState<CleanerResult | null>(null);
  const [overrides, setOverrides] = useState<Record<string, ItemAction>>({});
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeGroup, setActiveGroup] = useState<string>("");

  const analyzeFeedAction = useAction(api.feedCleaner.analyzeFeed);
  const bulkMoveToQueue = useMutation(api.feedItems.bulkMoveToQueue);
  const bulkDismiss = useMutation(api.feedItems.bulkDismiss);

  const analyze = useCallback(
    async (criteria: string) => {
      setIsAnalyzing(true);
      setError(null);
      setResult(null);
      setOverrides({});
      try {
        const data = await analyzeFeedAction({ criteria });
        setResult(data as CleanerResult);
        if ((data as CleanerResult).groups.length > 0) {
          setActiveGroup((data as CleanerResult).groups[0].name);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "Analysis failed");
      } finally {
        setIsAnalyzing(false);
      }
    },
    [analyzeFeedAction],
  );

  const getItemAction = useCallback(
    (id: string): ItemAction => {
      if (overrides[id]) return overrides[id];
      if (!result) return "dismiss";
      for (const group of result.groups) {
        const item = group.items.find((i) => i.id === id);
        if (item) return item.action;
      }
      return "dismiss";
    },
    [result, overrides],
  );

  const toggleItem = useCallback(
    (id: string) => {
      const currentAction = getItemAction(id);
      setOverrides((prev) => ({
        ...prev,
        [id]: currentAction === "queue" ? "dismiss" : "queue",
      }));
    },
    [getItemAction],
  );

  const { queueCount, dismissCount } = useMemo(() => {
    if (!result) return { queueCount: 0, dismissCount: 0 };
    let queue = 0;
    let dismiss = 0;
    for (const group of result.groups) {
      for (const item of group.items) {
        const action = overrides[item.id] ?? item.action;
        if (action === "queue") queue++;
        else dismiss++;
      }
    }
    return { queueCount: queue, dismissCount: dismiss };
  }, [result, overrides]);

  const apply = useCallback(async () => {
    if (!result) return;
    setIsApplying(true);
    setError(null);

    try {
      const queueIds: Id<"feedItems">[] = [];
      const dismissIds: Id<"feedItems">[] = [];

      for (const group of result.groups) {
        for (const item of group.items) {
          const action = overrides[item.id] ?? item.action;
          if (action === "queue") {
            queueIds.push(item.id as Id<"feedItems">);
          } else {
            dismissIds.push(item.id as Id<"feedItems">);
          }
        }
      }

      if (queueIds.length > 0) {
        await bulkMoveToQueue({ itemIds: queueIds });
      }
      if (dismissIds.length > 0) {
        await bulkDismiss({ itemIds: dismissIds });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to apply changes");
    } finally {
      setIsApplying(false);
    }
  }, [result, overrides, bulkMoveToQueue, bulkDismiss]);

  const reset = useCallback(() => {
    setResult(null);
    setOverrides({});
    setIsAnalyzing(false);
    setIsApplying(false);
    setError(null);
    setActiveGroup("");
  }, []);

  return {
    result,
    overrides,
    isAnalyzing,
    isApplying,
    error,
    activeGroup,
    setActiveGroup,
    analyze,
    toggleItem,
    getItemAction,
    apply,
    reset,
    queueCount,
    dismissCount,
  };
}
